
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {ChevronLeft, ChevronRight} from 'lucide-react'
import { lumi } from '../../../lib/lumi'

interface ProductCarouselV2Props {
  currentLang: 'zh' | 'en'
}

interface Product {
  _id: string
  productId: number
  name: string
  nameEn?: string
  price: number
  images: string[]
  category: string
  slug?: string
  status?: string
  inventory?: number
}

const ProductCarouselV2: React.FC<ProductCarouselV2Props> = ({ currentLang }) => {
  const [products, setProducts] = useState<Product[]>([])
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    lumi.entities.products
      ?.list?.({
        filter: { status: 'active' },
        limit: 8,
        sort: { isFeatured: -1, createdAt: -1 },
      })
      .then((res: any) => {
        if (res?.list) setProducts(res.list)
      })
      .catch(() => setProducts([]))
  }, [])

  const scroll = (dir: 'prev' | 'next') => {
    const el = scrollerRef.current
    if (!el) return
    const step = el.clientWidth * 0.8
    el.scrollBy({ left: dir === 'next' ? step : -step, behavior: 'smooth' })
  }

  if (products.length === 0) return null

  return (
    <section className="bg-[#FAF9F1] py-20 md:py-28">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        {/* Heading — centered, serif italic — Barista Map style */}
        <div className="mb-12 text-center">
          <h2
            className="text-black"
            style={{
              fontFamily: "'EB Garamond', serif",
              fontWeight: 400,
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              letterSpacing: '0.02em',
            }}
          >
            Shop
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Arrows */}
          <button
            type="button"
            onClick={() => scroll('prev')}
            className="absolute -left-2 top-[35%] z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-black shadow-md transition-colors hover:bg-white md:left-0"
            aria-label="Previous"
          >
            <ChevronLeft size={20} strokeWidth={1.25} />
          </button>
          <button
            type="button"
            onClick={() => scroll('next')}
            className="absolute -right-2 top-[35%] z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-black shadow-md transition-colors hover:bg-white md:right-0"
            aria-label="Next"
          >
            <ChevronRight size={20} strokeWidth={1.25} />
          </button>

          {/* Scroller */}
          <div
            ref={scrollerRef}
            className="flex gap-6 overflow-x-auto scroll-smooth pb-4 [scrollbar-width:none] [-ms-overflow-style:none]"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <style>{`
              .v2-product-scroller::-webkit-scrollbar { display: none; }
            `}</style>

            {products.map((product) => {
              const productUrl = `/${currentLang}/shop/${product.category}/${product.productId || product._id}/${product.slug || 'product'}`
              const isOutOfStock = product.status === 'out_of_stock' || product.inventory === 0
              const displayName = currentLang === 'en' ? product.nameEn || product.name : product.name

              return (
                <Link
                  key={product._id}
                  to={productUrl}
                  className="group flex-shrink-0 basis-[80%] sm:basis-[45%] md:basis-[30%] lg:basis-[24%]"
                >
                  {/* Image — square, white background, badge overlay */}
                  <div className="relative aspect-square overflow-hidden bg-white">
                    <img
                      src={product.images?.[0] || ''}
                      alt={displayName}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    {isOutOfStock && (
                      <div className="absolute left-3 top-3 bg-black px-3 py-1 text-xs tracking-wider text-white">
                        {currentLang === 'zh' ? '售完' : 'Sold Out'}
                      </div>
                    )}
                  </div>

                  {/* Title + price — minimalist */}
                  <div className="pt-4">
                    <h3
                      className="mb-2 text-sm leading-snug text-black/85"
                      style={{ letterSpacing: '0.02em' }}
                    >
                      {displayName}
                    </h3>
                    <p
                      className="text-sm text-black/70"
                      style={{ fontFamily: "'EB Garamond', serif" }}
                    >
                      NT$ {product.price?.toLocaleString?.()}
                    </p>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* View all */}
        <div className="mt-12 text-center">
          <Link
            to={`/${currentLang}/shop`}
            className="group inline-flex items-center text-sm tracking-[0.2em] text-black"
            style={{ fontFamily: "'EB Garamond', serif" }}
          >
            <span className="border-b border-black pb-1 transition-all duration-300 group-hover:tracking-[0.3em]">
              View all
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}

export default ProductCarouselV2
